import {Component, Inject, Input, OnInit} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ProdDialogData, ProdDialogService} from "prod-dialog";
import {GridFilterEvent, HttpFilterParam} from "prod-shared";
import {ClientFilter, ClientFilterService, ProdGridColumnType, ProdGridComponent} from 'prod-grid';
import {DipColumn} from "../../../dip-column";
import {AbstractProdForm, ProdFormField, ProdFormFieldType, ProdFormValidators, RequiredValidator} from 'prod-form';
import {Subject, Subscription} from "rxjs";
import {OrganizationService} from "../../../../services/organization.service";
import {ClientFilterData} from "../../service/dip-client-filter.service";
import {RoleService} from "../../../../services/role.service";
import {Role} from "../../../role";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'dip-grid-filter-form',
  templateUrl: './dip-grid-filter-form.html'
})

export class DipGridFilterForm extends AbstractProdForm implements OnInit {
  @Input() deletedFilters$: Subject<number>;
  grid: ProdGridComponent;
  lastFilter: ClientFilter;
  clientFilters: ClientFilterData[] = [];
  fields: ProdFormField[] = [
    ProdFormField.create({
      name: 'name',
      label: this.translate.instant('grid_filter.form.name'),
      type: ProdFormFieldType.TEXT,
      value: this.dialogData.data.lastActivatedClientFilter ? this.dialogData.data.lastActivatedClientFilter.name : '',
      validators: new ProdFormValidators([
        new RequiredValidator()
      ]),
    }),
    ProdFormField.create({
      name: 'user',
      label: '',
      value: this.dialogData.data.lastActivatedClientFilter ? (this.dialogData.data.lastActivatedClientFilter.user ? 'private' : 'public') : 'private',
      type: ProdFormFieldType.RADIO_GROUP,
      radioGroupParameters: {
        radioButtons: [
          {label: this.translate.instant('grid_filter.form.private'), value: 'private'},
          {label: this.translate.instant('grid_filter.form.public'), value: 'public'}
        ]
      }
    }),
    ProdFormField.create({
      type: ProdFormFieldType.HIDDEN,
      name: 'organizations',
      label: this.translate.instant('grid_filter.form.organizations'),
      value: this.dialogData.data.lastActivatedClientFilter ? this.getOrganizationFormValues() : undefined,
      gridParameter: {
        displayColumn: DipColumn.get({property: 'name', type: ProdGridColumnType.STRING}),
        service: OrganizationService,
        filter: new HttpFilterParam({
          property: 'organizationType.id',
          operator: 'eq',
          value: 1
        })
      },
      isEditable: true
    })
  ];
  typeChangeSubscription$: Subscription;

  constructor(dialogRef: MatDialogRef<DipGridFilterForm>,
              @Inject(MAT_DIALOG_DATA) public dialogData: ProdDialogData,
              private filterService: ClientFilterService,
              private roleService: RoleService,
              private dialogService: ProdDialogService,
              private translate: TranslateService) {
    super();
  }

  ngOnInit() {
    this.grid = this.dialogData.data.grid;
    this.clientFilters = this.dialogData.data.clientFilters;
    this.deletedFilters$.subscribe(id => {
      this.clientFilters = this.clientFilters.filter(f => f.id !== id);
    });
  }

  override submit(event) {
    const payload = event.formData
    let data = {};
    data['component'] = this.grid.parentComponentName;
    data['name'] = payload.name;
    data['user'] = null;
    data['organizations'] = [];
    if (payload.user === 'private') {
      data['user'] = JSON.parse(localStorage.getItem('current_user')).id;
    } else {
      if (!this.roleService.hasRole([Role.ROLE_CLIENT_FILTER_EDIT])) {
        this.dialogService.alert({
          title: this.translate.instant('general.access_denied'),
          message: this.translate.instant('grid_filter.form.no_perm_alert')
        });
        return;
      }
      for (const [key, value] of Object.entries(payload.organizations)) {
        if (value['checked']) data['organizations'].push(+key);
      }
    }

    data['jsonFilter'] = JSON.stringify({
      properties: this.grid.filtering.filters,
      sorters: this.grid.sorter.sorting,
      limit: this.grid.matPaginator ? this.grid.matPaginator.pageSize : null
    });

    const clientFilter = this.clientFilters.filter(c => (c.user !== null ? c.user.id : null) === data['user'] && c.name === data['name']);
    if (clientFilter.length === 1) {
      this.dialogService.confirm(this.translate.instant('grid_filter.form.confirm_update')).subscribe(closed => {
        if (closed === true) {
          this.filterService.patch(data, clientFilter[0].id).subscribe(res => {
            if (res.success) {
              this.dialogData.data.filterEvents.emit('patched');
              this.data.formResponseEvent.emit();
            }
          });
        }
      });
    } else {
      this.filterService.post(data).subscribe(res => {
        if (res.success) {
          this.dialogData.data.filterEvents.emit({name: GridFilterEvent.ADD});
          this.data.formResponseEvent.emit();
          let clientFilter = new ClientFilterData();
          clientFilter.id = res.data.id;
          clientFilter.user = res.data.user;
          clientFilter.name = res.data.name;
          this.clientFilters.push(clientFilter);
        }
      });
    }
  }

  onFormOutput(form) {
    const organizationsField = this.fields.find(field => field.name === 'organizations');

    if (form.get('user').value === 'public') organizationsField.type = ProdFormFieldType.GRID;

    this.typeChangeSubscription$ = form.get('user').valueChanges.subscribe(value => {
      if (value === 'private') {
        organizationsField.type = ProdFormFieldType.HIDDEN;
      } else {
        organizationsField.type = ProdFormFieldType.GRID;
      }
    })
  }

  getOrganizationFormValues() {
    let organizations = {};
    organizations[0] = [];
    this.dialogData.data.lastActivatedClientFilter.organizations.forEach(organization => {
      organizations[0].push(organization);
    });

    return organizations;
  }

  ngOnDestroy() {
    if (this.typeChangeSubscription$) this.typeChangeSubscription$.unsubscribe();
  }
}
